import {
	Disclosure,
	DisclosureButton,
	DisclosurePanel,
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
} from "@headlessui/react";
import { Bars3Icon, BellIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { LuUser } from "react-icons/lu";
import { FaShoppingCart } from "react-icons/fa";
import { Outlet, useNavigate, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import onelyk from "../../../src/Assets/onelyklogo.svg";
import changeStatusIcon from "../../Assets/changeStatus.svg";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { useSocket } from "../../Contexts/SocketContext/SocketContext";
import { baseUrl } from "../../Environments/environment";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import messageSound from "../../Assets/windows_sms.mp3";
import ChatListModal from "./Message/ChatListModal/ChatListModal";
import { toast } from "react-toastify";
import UpdatePropertyModal from "../PropertySetup/UpdateProperty/UpdateProperty";
import { LocalStorage } from "../../utils";
import { apiClient } from "../../api";
import CreditsBadge from "../CreditSetup/CreditsBadge";
// import GlobalChatModal from "./Modal/GlobalChatModal";

const user = {
	name: "Tom Cook",
	email: "tom@example.com",
	imageUrl:
		"https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
};
const navigation = [
	{ name: "Home", href: "/dashboard", current: true },
	{ name: "Analytics", href: "/dashboard/review-overview", current: false },
	// { name: "Settings", href: "/dashboard/configure/template", current: false },
	{ name: "Reviews", href: "/dashboard/reviews" },
];

function classNames(...classes) {
	return classes.filter(Boolean).join(" ");
}
function requestNotificationPermission() {
	if ("Notification" in window) {
		Notification.requestPermission().then((permission) => {
			if (permission === "granted") {
				console.log("Notification permission granted.");
			} else {
				console.log("Notification permission denied.");
			}
		});
	} else {
		console.log("This browser does not support notifications.");
	}
}
const fetchChatList = async (baseUrl, selectedPropertyId, accessToken) => {
	const response = await apiClient.get(
		`${baseUrl}/chatList/${selectedPropertyId}`,
	);
	if (response.ok) {
		const newToken = response.headers.get("Authorization");
		if (newToken) {
			localStorage.setItem("accessToken", newToken.split(" ")[1]); // Save the new token
		}
	}
	// console.log(response.data.result);
	return response.data.result;
};
function showNotification(title, message) {
	if (Notification.permission === "granted") {
		const notification = new Notification(title, {
			body: message, // Message body
		});

		notification.onclick = () => {
			window.focus();
		};
	}
}

const useReactQuerySubscription = (
	socket,
	baseUrl,
	selectedPropertyId,
	setWalletCredits,
) => {
	const queryClient = useQueryClient();

	const messageAudioRef = useRef(null);

	useEffect(() => {
		messageAudioRef.current = new Audio(messageSound);

		return () => {
			if (messageAudioRef.current) {
				messageAudioRef.current.pause();
				messageAudioRef.current.src = "";
				messageAudioRef.current.load();
				messageAudioRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		requestNotificationPermission();
	}, []);

	useEffect(() => {
		socket.on("chatList:update", (update) => {
			queryClient.invalidateQueries({
				queryKey: ["chatList", selectedPropertyId],
			});

			if (update?.chatList?.unreadMessages > 0) {
				showNotification("New Message", "You have unread messages");
			}

			if (typeof update === "object" && Object.keys(update).length === 0) {
				if (messageAudioRef.current) {
					messageAudioRef.current.currentTime = 0;
					messageAudioRef.current.play();
				}
			}
		});

		socket.on("message:newMessage", (newMessage) => {
			queryClient.invalidateQueries({
				queryKey: ["chatList", selectedPropertyId],
			});

			if (newMessage?.message?.senderId !== selectedPropertyId) {
				queryClient.invalidateQueries([
					"messages",
					selectedPropertyId,
					newMessage?.message?.guestId,
				]);
				showNotification("New Message", "You have unread messages");
				if (messageAudioRef.current) {
					messageAudioRef.current.currentTime = 0;
					messageAudioRef.current.play();
				}
			}
		});

		socket.on("wallet:creditsUpdated", (data) => {
			console.log("Credits updated:", data.credits);
			setWalletCredits(data.credits);
		});

		return () => {
			socket.off("chatList:update");
			socket.off("message:newMessage");
			socket.off("wallet:creditsUpdated");
		};
	}, [socket, queryClient, baseUrl, selectedPropertyId]);
};

export default function Dashboard() {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { logout } = useAuth();
	const {
		selectedPropertyId,
		setPropertyName,
		setPropertyTimezone,
		setPropertyChats,
		selectProperty,
	} = useProperty();
	const accessToken = LocalStorage.get("accessToken");
	const { socket } = useSocket();
	const navigate = useNavigate();
	const [verificationStatus, setVerificationStatus] = useState(null);
	const [loading, setLoading] = useState(true);
	const [walletCredits, setWalletCredits] = useState(0);
	const [propertiesList, setPropertiesList] = useState([]);

	useEffect(() => {
		// Load properties from localStorage
		const storedProperties = localStorage.getItem("propertiesList");
		if (storedProperties) {
			try {
				const parsedProperties = JSON.parse(storedProperties);
				setPropertiesList(parsedProperties);
			} catch (error) {
				console.error("Error parsing properties list:", error);
			}
		}
	}, []);

	useEffect(() => {
		if (!selectedPropertyId) {
			navigate("/propertyhome");
			toast.warn("Select the property first!!");
		}
	}, [selectedPropertyId, navigate]);
	useEffect(() => {
		if (selectedPropertyId) {
			fetchPropertyData(selectedPropertyId);
			fetchWalletCredits(selectedPropertyId);
		}
	}, [selectedPropertyId]);
	const fetchPropertyData = async (propertyId) => {
		setLoading(true);
		try {
			const response = await apiClient.get(`${baseUrl}/property/${propertyId}`);

			if (response.status === 200) {
				const { property } = response.data.result.property;
				const { setting } = response.data.result;
				setPropertyName(property.name);
				setPropertyTimezone(setting.timezone);
			}
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || "Error fetching property data.";
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};
	const fetchWalletCredits = async (propertyId) => {
		try {
			const response = await apiClient.get(
				`${baseUrl}/stripe/wallet/${propertyId}`,
			);
			setWalletCredits(response.data.result.credits);
		} catch (err) {
			toast.error("Failed to load wallet credits");
		}
	};
	useReactQuerySubscription(
		socket,
		baseUrl,
		selectedPropertyId,
		setWalletCredits,
	);

	const {
		data: chats = {},
		isLoading,
		error,
	} = useQuery({
		queryKey: ["chatList", selectedPropertyId],
		queryFn: () => fetchChatList(baseUrl, selectedPropertyId, accessToken),
		staleTime: 0,
		cacheTime: 0,
	});
	useEffect(() => {
		if (chats) {
			setPropertyChats(chats);
		}
	}, [chats, setPropertyChats]);

	const userNavigation = [
		{ name: "Listing Profile", href: "/dashboard/updateproperty" },
		{ name: "Wallet", href: "/dashboard/wallet" },
		{ name: "Settings", href: "/dashboard/configure/template" },
		...(propertiesList.length <= 1 ? [{ name: "Add Property", href: "/addproperty" }] : []),
		{
			name: "Sign out",
			href: "#",
			onClick: async () => {
				await logout();
				window.location.href = "/";
			},
		},
	];

	const handleOpenModal = () => {
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const handlePropertySelect = (property) => {
		selectProperty(property._id);
		toast.success(`Switched to ${property.name}`);
		// The useEffect will automatically refresh data when selectedPropertyId changes
	};

	useEffect(() => {
		const fetchTollFreeVerificationStatus = async () => {
			try {
				// console.log("morning", response.data.result.status);
			} catch (error) {
				setLoading(false);
				const errorMessage =
					error.response?.data?.message ||
					"Failed to fetch toll-free verification status. Please try again.";
				toast.error(errorMessage);
			}
		};

		if (selectedPropertyId && accessToken) {
			fetchTollFreeVerificationStatus();
		}
	}, [selectedPropertyId, accessToken]);

	// setVerificationStatus('TWILIO_APPROVED');
	return (
		<>
			<div className="min-h-full">
				{/* {loading && (
          <div className="bg-white p-1 rounded-md text-center my-">
            <span className="text-red-600 text-sm">
              Please wait we are checking twilio verification..
            </span>
          </div>
        )} */}

				<div className="bg-indigo-600 pb-20 sm:pb-24 lg:pb-32">
					<Disclosure
						as="nav"
						className="border-b border-indigo-300 border-opacity-25 bg-indigo-600 lg:border-none relative"
					>
						<div className="mx-auto max-w-7xl px-2 sm:px-4 lg:px-8">
							<div className="relative flex h-14 sm:h-16 items-center justify-between lg:border-indigo-400 lg:border-opacity-25 mb-2 sm:mb-4">
								<div className="flex items-center px-2 lg:px-0">
									<div className="flex-shrink-0">
										<img
											alt="Your Company"
											src={onelyk}
											className="block h-9 w-auto sm:h-11 sm:w-auto lg:h-10 lg:w-auto"
										/>
									</div>
									{/* Desktop Navigation - Show on large screens */}
									<div className="hidden lg:ml-10 lg:block">
										<div className="flex space-x-2 xl:space-x-4">
											{navigation.map((item) => {
												const isActive = window.location.pathname === item.href;
												return (
													<Link
														key={item.name}
														to={item.href}
														aria-current={isActive ? "page" : undefined}
														className={classNames(
															isActive
																? "bg-indigo-700 text-white"
																: "text-white hover:bg-indigo-500 hover:bg-opacity-75",
															"rounded-md px-2 xl:px-3 py-2 text-sm font-medium transition-colors",
														)}
													>
														{item.name}
													</Link>
												);
											})}
										</div>
									</div>
								</div>
								{/* search */}
								{/* <div className="flex flex-1 justify-center px-2 lg:ml-6 lg:justify-end">
                  <div className="w-full max-w-lg lg:max-w-xs">
                    <label htmlFor="search" className="sr-only">
                      Search
                    </label>
                    <div className="relative text-gray-400 focus-within:text-gray-600">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MagnifyingGlassIcon
                          aria-hidden="true"
                          className="h-5 w-5"
                        />
                      </div>
                      <input
                        id="search"
                        name="search"
                        type="search"
                        placeholder="Search"
                        className="block w-full rounded-md border-0 bg-white py-1.5 pl-10 pr-3 text-gray-900 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                </div> */}
								<div className="flex items-center gap-2 sm:gap-3">
									{/* Credits and User Menu */}
									<div className="flex items-center gap-2 xl:gap-3">
										{/* <button
                      type="button"
                      className="relative flex-shrink-0 rounded-full bg-indigo-600 p-1 text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600"
                    >
                      <span className="absolute -inset-1.5" />
                      <span className="sr-only">View notifications</span>
                      <BellIcon aria-hidden="true" className="h-6 w-6" />
                    </button> */}

									{/* Switch Property Menu - only when more than one property */}
									<div className="hidden lg:flex lg:items-center lg:gap-3">
									{propertiesList.length > 1 && (
									<Menu as="div" className="relative flex-shrink-0">
										<MenuButton className="relative flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600 transition-colors">
											<img
												src={changeStatusIcon}
												alt="Switch property"
												className="h-4 w-5 brightness-0 invert"
											/>
											<span className="text-sm font-medium whitespace-nowrap">
												Switch property
											</span>
										</MenuButton>
										<MenuItems
											transition
											className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in max-h-96 overflow-y-auto"
										>
											{/* Add Property Menu Item */}
											<MenuItem>
												{({ focus }) => (
													<Link
														to="/addproperty"
														className={classNames(
															focus ? "bg-indigo-50" : "",
															"block w-full text-left px-4 py-3 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors border-b border-gray-200",
														)}
													>
														+ Add Property
													</Link>
												)}
											</MenuItem>
											{propertiesList.length > 0 ? (
												propertiesList.map((property) => {
													const location = [property.city, property.zipCode || property.state].filter(Boolean).join(", ") || property.address || "";
													const isSelected = selectedPropertyId === property._id;
													return (
														<MenuItem key={property._id}>
															{({ focus }) => (
																<button
																	onClick={() => handlePropertySelect(property)}
																	className={classNames(
																		focus ? "bg-indigo-50" : "",
																		isSelected ? "bg-indigo-100" : "",
																		"block w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors",
																	)}
																>
																	<p className={classNames("truncate text-sm font-normal", isSelected ? "text-indigo-600" : "text-gray-900")}>
																		{property.name}
																	</p>
																	{location ? (
																		<p className={classNames("text-xs truncate mt-0.5", isSelected ? "text-indigo-600" : "text-gray-500")}>
																			{location}
																		</p>
																	) : null}
																</button>
															)}
														</MenuItem>
													);
												})
											) : (
												<div className="px-4 py-3 text-sm text-gray-500 text-center">
													No properties available
												</div>
											)}
										</MenuItems>
									</Menu>
									)}

									{/* QR Code Kit Cart Icon */}
									<Link
										to="/dashboard/qr-code-kit"
										className="relative flex flex-shrink-0 items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600 transition-colors"
									>
										<FaShoppingCart
											className="h-4 w-4 sm:h-5 sm:w-5"
											aria-hidden="true"
										/>
										<span className="text-sm font-medium whitespace-nowrap">
											Shop QR Kit
										</span>
									</Link>
									</div>
								

										<CreditsBadge credits={walletCredits} />
										{/* User Menu - Hidden on small screens, visible on large screens */}
										<div className="hidden lg:block">
											<Menu
												as="div"
												className="relative ml-0 sm:ml-2 xl:ml-3 flex-shrink-0"
											>
												<div>
													<MenuButton className="relative flex rounded-full bg-indigo-600 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600">
														<span className="absolute -inset-1.5" />
														<span className="sr-only">Open user menu</span>

														<div className="inline-flex items-center justify-center rounded-full bg-white p-1.5 sm:p-2 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors">
															<LuUser
																className="h-4 w-4 sm:h-5 sm:w-5"
																aria-hidden="true"
																color="#4f46e5"
															/>
														</div>
													</MenuButton>
												</div>
												<MenuItems
													transition
													className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
												>
													{userNavigation.map((item) => (
														<MenuItem key={item.name}>
															{({ focus }) => (
																<Link
																	to={item.href}
																	onClick={item.onClick}
																	className={classNames(
																		focus ? "bg-gray-100" : "",
																		"block px-4 py-2 text-sm text-gray-700",
																	)}
																>
																	{item.name}
																</Link>
															)}
														</MenuItem>
													))}
												</MenuItems>
											</Menu>
										</div>
									</div>
									{/* Mobile menu button - only visible on small screens */}
									<div className="flex lg:hidden">
										<DisclosureButton className="group relative inline-flex items-center justify-center rounded-md bg-indigo-600 p-2 text-indigo-200 hover:bg-indigo-500 hover:bg-opacity-75 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600">
											<span className="absolute -inset-0.5" />
											<span className="sr-only">Open main menu</span>
											<Bars3Icon
												aria-hidden="true"
												className="block h-6 w-6 group-data-[open]:hidden"
											/>
											<XMarkIcon
												aria-hidden="true"
												className="hidden h-6 w-6 group-data-[open]:block"
											/>
										</DisclosureButton>
									</div>
								</div>
							</div>
						</div>

						{/* Mobile Menu Pop-up - only visible on small screens */}
						<DisclosurePanel className="lg:hidden absolute top-full left-2 sm:left-4 right-2 sm:right-4 z-50 mt-2">
							{/* Mobile Menu - Show all navigation options */}
							<div className="rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
								{/* Main Navigation Links */}
								<div className="border-b border-gray-200 pb-1">
									{navigation.map((item) => {
										
										const isActive = window.location.pathname === item.href;
										return (
											<a
												key={item.name}
												href={item.href}
												className={classNames(
													isActive
														? "bg-indigo-50 text-indigo-600"
														: "text-gray-700 hover:bg-gray-100",
													"block px-4 py-2 text-sm font-medium transition-colors",
												)}
											>
												{item.name}
											</a>
										);
										
									})}
									<a
												key={"Shop QR Kit"}
												href={"/dashboard/qr-code-kit"}
												className={classNames(
													"text-gray-700 hover:bg-gray-100",
													"block px-4 py-2 text-sm font-medium transition-colors",
												)}
											>
												Shop QR Kit
											</a>
								</div>
								{propertiesList.length > 1 && (
								<Menu as="div" className="relative ">
										<MenuButton className='text-gray-700 	block px-4 py-2 text-sm font-medium transition-colors'>
											
											Switch property
											
										</MenuButton>
										<MenuItems
											transition
											className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in max-h-96 overflow-y-auto"
										>
											{/* Add Property Menu Item */}
											<MenuItem>
												{({ focus }) => (
													<Link
														to="/addproperty"
														className={classNames(
															focus ? "bg-indigo-50" : "",
															"block w-full text-left px-4 py-3 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors border-b border-gray-200",
														)}
													>
														+ Add Property
													</Link>
												)}
											</MenuItem>
											{propertiesList.length > 0 ? (
												propertiesList.map((property) => {
													const location = [property.city, property.zipCode || property.state].filter(Boolean).join(", ") || property.address || "";
													const isSelected = selectedPropertyId === property._id;
													return (
														<MenuItem key={property._id}>
															{({ focus }) => (
																<button
																	onClick={() => handlePropertySelect(property)}
																	className={classNames(
																		focus ? "bg-indigo-50" : "",
																		isSelected ? "bg-indigo-100" : "",
																		"block w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors",
																	)}
																>
																	<p className={classNames("truncate text-sm font-normal", isSelected ? "text-indigo-600" : "text-gray-900")}>
																		{property.name}
																	</p>
																	{location ? (
																		<p className={classNames("text-xs truncate mt-0.5", isSelected ? "text-indigo-600" : "text-gray-500")}>
																			{location}
																		</p>
																	) : null}
																</button>
															)}
														</MenuItem>
													);
												})
											) : (
												<div className="px-4 py-3 text-sm text-gray-500 text-center">
													No properties available
												</div>
											)}
										</MenuItems>
									</Menu>
								)}
								{/* User Navigation Links */}
								<div className="pt-1">
									{userNavigation.map((item) => (
										<a
											key={item.name}
											href={item.href}
											onClick={(e) => {
												if (item.onClick) {
													e.preventDefault();
													item.onClick();
												}
											}}
											className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
										>
											{item.name}
										</a>
									))}
								</div>
							</div>
						</DisclosurePanel>
					</Disclosure>
					{/* <header className="py-10">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <h1 className="text-3xl font-bold tracking-tight text-white">Dashboard</h1>
            </div>
          </header> */}
				</div>

				<main className="-mt-20 sm:-mt-24 lg:-mt-32">
					<div className="mx-auto max-w-7xl px-2 sm:px-4 pb-8 sm:pb-12 lg:px-8">
						{/* <div className="rounded-lg bg-white px-5 py-6 shadow sm:px-6"> */}
						<div className="rounded-lg bg-[#FFF] px-2 sm:px-4 py-2 sm:py-4 shadow">
							{/* Your content */}

							{/* <Home/> */}
							<Outlet />
						</div>
					</div>
				</main>

				{/* <button
          onClick={handleOpenModal}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-indigo-600 border-white border-2 text-white rounded-full p-2.5 sm:p-3 shadow-lg hover:bg-indigo-500 transition-all z-50"
          aria-label="Open messages"
        >
          <BiSolidMessageRoundedDetail className="h-6 w-6 sm:h-8 sm:w-8" />

          {chats.totalUnreadMessages > 0 && (
            <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-semibold rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center">
              {chats.totalUnreadMessages > 99
                ? "99+"
                : chats.totalUnreadMessages}
            </span>
          )}
        </button> */}

				{isModalOpen && (
					<ChatListModal onClose={handleCloseModal} chats={chats} />
				)}

				{/* {isUpdateModalOpen && (
          <UpdatePropertyModal
            selectedPropertyId={selectedPropertyId}
            accessToken={accessToken}
            onClose={() => setIsUpdateModalOpen(false)}
          />
        )} */}
			</div>
		</>
	);
}
